import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import inventoryService from '../services/inventoryService';
import CollectionIcon from './CollectionIcon';
import './CardQuantityAdjuster.css';

/**
 * A reusable component that handles card quantity adjustments in inventory
 * 
 * @param {Object} props
 * @param {Object} props.card - The card to adjust quantity for
 * @param {boolean} props.disabled - Whether the buttons should be disabled
 * @param {string} props.className - Additional classes to apply
 * @param {number} props.size - Size in pixels for the buttons (default: 28)
 */
const CardQuantityAdjuster = ({ 
  card,
  disabled = false, 
  className = '',
  size = 28
}) => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  // Use the card's quantity property for the initial state
  const [quantity, setQuantity] = useState(card?.quantity || 0);

  // Common auth check function
  const checkAuth = (action) => {
    if (!currentUser) {
      navigate('/login', { 
        state: { 
          from: window.location.pathname,
          message: `Please log in to ${action} your collection`
        } 
      });
      return false;
    }
    return true;
  };

  // Handle add to inventory
  const handleAddToInventory = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check authentication
    if (!checkAuth('add items to')) return;

    if (!card || !card.id) {
      console.error('Invalid card provided to CardQuantityAdjuster:', card);
      return;
    }

    // Update local state immediately for responsive UI
    setQuantity(prevQuantity => prevQuantity + 1);
    
    try {
      await inventoryService.addToInventory(card, 1);
      showToast(`Added 1 ${card.name} to your collection`, 'success');
    } catch (err) {
      console.error('Error adding to inventory:', card.id, err.response ? err.response.data : err.message);
      showToast('Failed to add card to your collection', 'error');
      // Rollback UI state to previous quantity
      setQuantity(prevQuantity => prevQuantity - 1);
    }
  };

  // Handle remove from inventory
  const handleRemoveFromInventory = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check authentication
    if (!checkAuth('manage')) return;

    if (!card || !card.id || quantity <= 0) {
      return;
    }

    // Update local state immediately for responsive UI
    setQuantity(prevQuantity => prevQuantity - 1);
    
    try {
      await inventoryService.removeFromInventory(card, 1);
      showToast(`Removed 1 ${card.name} from your collection`, 'info');
    } catch (err) {
      console.error('Error removing from inventory:', card.id, err.response ? err.response.data : err.message);
      showToast('Failed to remove card from your collection', 'error');
      // Rollback UI state to previous quantity
      setQuantity(prevQuantity => prevQuantity + 1);
    }
  };

  if (!currentUser) {
    return <></>;
  }

  return (
    <div className={`card-quantity-adjuster ${className}`}>
      <div className="card-quantity-adjuster__icon">
        <CollectionIcon 
          size={size} 
          isActive={quantity > 0}
        />
      </div>
      
      <button 
        className="card-quantity-adjuster__remove-btn"
        title="Remove from Collection"
        onClick={handleRemoveFromInventory}
        disabled={disabled || quantity === 0}
      >
        −
      </button>
      
      {quantity >= 0 && (
        <span 
          className="card-quantity-adjuster__quantity-display"
        >
          {quantity}
        </span>
      )}
      
      <button 
        className="card-quantity-adjuster__add-btn"
        title="Add to Collection"
        onClick={handleAddToInventory}
        disabled={disabled}
      >
        +
      </button>
    </div>
  );
};

export default CardQuantityAdjuster;